import React, { useState, useEffect } from 'react';
import { Edit2, X, Eye, Check, Loader, Download, Mail, DownloadCloud } from 'lucide-react';
import FormSection from './modules/FormSection';
import PDFPreviewModal from './modules/PreviewPDF';
import initialFormData from './modules/InitialFormData';
import { useParams } from 'react-router-dom';
import SignatureSection from './modules/SignatureForm';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  updateFormDataById,
  finalizeApplication,
} from '../../../services/Queries/SupabaseQueries';

const API_BASE_URL = 'https://api-backend-samah.vercel.app/api/pdf';

const HRFormPage = () => {
  const { uuid } = useParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [isEditable, setIsEditable] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    coverSheet: true,
    applicationForEmployment: true,
    w4: false,
    i9: false,
    signatures: false,
  });
  const [originalFormData, setOriginalFormData] = useState(null);
  const [applicationId, setApplicationId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState('draft');
  const [finalizing, setFinalizing] = useState(false);

  const [sendingRequest, setSendingRequest] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState(null);

  useEffect(() => {
    const fetchApplicationData = async () => {
      if (!uuid) return;

      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(`${API_BASE_URL}/forms/${uuid}`);
        const data = response.data?.data;

        if (data) {
          console.log('Fetched application data:', data);
          const id = data.id;
          setApplicationId(id);
          setStatus(data.status || 'draft');

          setFormData((prev) => ({ ...prev, ...data.form_data }));
          setOriginalFormData((prev) => ({ ...prev, ...data.form_data }));
        } else {
          setError('Application not found');
        }
      } catch (err) {
        console.error('Error fetching form data:', err);
        const message =
          err.response?.data?.message || err.message || 'Failed to load application';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    if (uuid) fetchApplicationData();
  }, [uuid]);

  const handleEditClick = () => {
    if (status === 'finalized') {
      toast.error('This application has been finalized and cannot be edited.');
      return;
    }
    setOriginalFormData({ ...formData });
    setIsEditable(true);
  };

  const handleCancelEdit = () => {
    if (originalFormData) setFormData(originalFormData);
    setIsEditable(false);
    setOriginalFormData(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSignatureChange = (signatureData) => {
    setFormData((prev) => ({ ...prev, ...signatureData }));
  };

  const handleSave = async () => {
    if (!applicationId) {
      toast.error('Application ID missing. Cannot save.');
      return;
    }

    if (!formData.hrSignature) {
      toast.error('HR signature is required. Please sign the form before saving.');
      return;
    }

    setSaving(true);
    try {
      await updateFormDataById(applicationId, formData);
      setOriginalFormData({ ...formData });
      setIsEditable(false);
      toast.success('Form saved successfully!');
    } catch (err) {
      console.error('Save failed:', err);
      toast.error('Failed to save form: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!applicationId) {
      toast.error('Application ID missing. Cannot finalize.');
      return;
    }

    if (!formData.hrSignature) {
      toast.error('HR signature is required. Please sign before finalizing.');
      return;
    }

    if (status === 'finalized') {
      toast.info('This application is already finalized.');
      return;
    }

    const confirmed = window.confirm(
      'Finalize this application?\n\n' +
        'Once finalized, the form cannot be edited. Make sure all information is correct.'
    );
    if (!confirmed) return;

    setFinalizing(true);
    setRequestMessage('Finalizing application…');

    try {
      const result = await finalizeApplication(applicationId, formData);
      setStatus(result.status || 'finalized');
      setOriginalFormData({ ...formData });
      setIsEditable(false);
      setRequestMessage('✅ Application finalized successfully.');
      toast.success('Application finalized.');
    } catch (err) {
      const msg = err?.message || 'Finalize failed';
      setRequestMessage(`❌ Finalize failed: ${msg}`);
      toast.error(`Finalize failed: ${msg}`);
    } finally {
      setFinalizing(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleGeneratePDF = async () => {
    if (!applicationId) {
      toast.error('Application ID missing. Cannot download PDF.');
      return;
    }

    if (!formData.hrSignature) {
      toast.error('HR signature is required. Please sign the form before downloading.');
      return;
    }

    try {
      setSendingRequest(true);
      setRequestMessage('Generating PDF via server...');
      toast.info('Requesting PDF from server...');
      const user = localStorage.getItem('sb-user');
      const hrId = user ? JSON.parse(user).id : null;
      const response = await axios.post(
        `${API_BASE_URL}/download/${applicationId}`,
        {
          hr_id: hrId,
          hrSignature: formData.hrSignature,
        },
        { responseType: 'blob' }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formData.employeeName || 'PCA_Complete_Application'}_Reviewed.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setRequestMessage('PDF downloaded successfully!');
      toast.success('PDF downloaded!');
    } catch (err) {
      console.error('PDF download failed:', err);
      const errorMsg = err.response?.data?.message || err.message;
      setRequestMessage(`Download failed: ${errorMsg}`);
      toast.error(`Download failed: ${errorMsg}`);
    } finally {
      setSendingRequest(false);
    }
  };

  const handleSendReviewedEmail = async () => {
    if (!applicationId) {
      toast.error('Application ID missing. Cannot send email.');
      return;
    }

    if (!formData.hrSignature) {
      toast.error('HR signature is required. Please sign the form before sending email.');
      return;
    }

    setSendingRequest(true);
    setRequestMessage('Sending email with PDF...');
    setEmailError(null);
    setEmailSuccess(false);
    toast.info('Preparing email...');

    try {
      const emailTo = formData.email || 'info@samahomecare.com';
      const response = await axios.post(
        `${API_BASE_URL}/send-email/${applicationId}`,
        {
          email: emailTo,
          hrSignature: formData.hrSignature,
        }
      );

      if (response.data.success) {
        toast.success('Email sent successfully!');
        setEmailSuccess(true);
        setRequestMessage('✅ Application PDF emailed successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to send email');
      }
    } catch (err) {
      console.error('Email sending error:', err);
      const errorMsg = err.response?.data?.message || err.message;
      setEmailError(errorMsg);
      toast.error(`Error: ${errorMsg}`);
      setRequestMessage(`❌ Failed to send: ${errorMsg}`);
    } finally {
      setSendingRequest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading application...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center">
            <div className="bg-red-100 text-red-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <X size={32} />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Error Loading Application
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => (window.location.href = '/hr/forms')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Forms
            </button>
          </div>
        </div>
      </div>
    );
  }

  const sections = [
    {
      id: 'coverSheet',
      title: 'Cover Sheet',
      fields: [
        { label: 'Employee Name', name: 'employeeName', type: 'text' },
        { label: 'Date of Birth', name: 'dob', type: 'date' },
        { label: 'Gender', name: 'gender', type: 'select', options: ['', 'Male', 'Female', 'Other'] },
        { label: 'Address', name: 'address', type: 'text' },
        { label: 'Phone', name: 'employeePhone', type: 'tel' },
        { label: 'Email', name: 'email', type: 'email' },
        { label: 'Currently Working as PCA', name: 'currentlyWorkingPCA', type: 'select', options: ['', 'Yes', 'No'] },
        { label: 'Current Hours Per Week', name: 'currentHoursPerWeek', type: 'number' },
        { label: 'Client Name', name: 'clientName', type: 'text' },
        { label: 'Client DOB', name: 'clientDOB', type: 'date' },
        { label: 'Client Gender', name: 'clientGender', type: 'select', options: ['', 'Male', 'Female', 'Other'] },
        { label: 'Client Address', name: 'clientAddress', type: 'text' },
        { label: 'Client City', name: 'clientCity', type: 'text' },
        { label: 'Client State', name: 'clientState', type: 'text' },
        { label: 'Client Zip', name: 'clientZip', type: 'text' },
        { label: 'Client Phone', name: 'clientPhone', type: 'tel' },
        { label: 'Client Insurance', name: 'clientInsurance', type: 'text' },
        { label: 'Additional Info', name: 'additionalInfo', type: 'textarea' },
      ],
    },
    {
      id: 'applicationForEmployment',
      title: 'Application for Employment',
      fields: [
        { label: 'Last Name', name: 'lastName', type: 'text' },
        { label: 'First Name', name: 'firstName', type: 'text' },
        { label: 'Middle Initial', name: 'middleInitial', type: 'text' },
        { label: 'SSN', name: 'ssn', type: 'password' },
        { label: 'Street Address', name: 'streetAddress', type: 'text' },
        { label: 'City, State, Zip', name: 'cityStateZip', type: 'text' },
        { label: 'Phone Number', name: 'phoneNumber', type: 'tel' },
        { label: 'Legal Eligibility to Work', name: 'legalEligibility', type: 'select', options: ['', 'Yes', 'No'] },
        { label: 'Position Desired', name: 'positionDesired', type: 'text' },
        { label: 'Wage/Salary Desired', name: 'wageSalaryDesired', type: 'text' },
        { label: 'Employment Type', name: 'employmentType', type: 'select', options: ['', 'Full-time', 'Part-time', 'Temporary'] },
        { label: 'Start Date', name: 'startDate', type: 'date' },
        { label: 'Age', name: 'age', type: 'number' },
        { label: 'Enrolled in School', name: 'enrolledInSchool', type: 'select', options: ['', 'Yes', 'No'] },
        { label: 'School Details', name: 'schoolDetails', type: 'textarea' },
        { label: 'Job Skills', name: 'jobSkills', type: 'textarea' },
      ],
    },
    {
      id: 'w4',
      title: 'W-4 Form',
      fields: [
        { label: 'First Name', name: 'w4FirstName', type: 'text' },
        { label: 'Last Name', name: 'w4LastName', type: 'text' },
        { label: 'Address', name: 'w4Address', type: 'text' },
        { label: 'City', name: 'w4City', type: 'text' },
        { label: 'State', name: 'w4State', type: 'text' },
        { label: 'Zip', name: 'w4Zip', type: 'text' },
        { label: 'SSN', name: 'w4SSN', type: 'text' },
        { label: 'Filing Status', name: 'w4FilingStatus', type: 'select', options: ['', 'Single', 'Married', 'Head of Household'] },
        { label: 'Multiple Jobs', name: 'w4MultipleJobs', type: 'checkbox' },
        { label: 'Spouse Works', name: 'w4SpouseWorks', type: 'checkbox' },
        { label: 'Dependents Under 17', name: 'w4DependentsUnder17', type: 'number' },
        { label: 'Other Dependents', name: 'w4OtherDependents', type: 'number' },
        { label: 'Other Income', name: 'w4OtherIncome', type: 'text' },
        { label: 'Deductions', name: 'w4Deductions', type: 'text' },
        { label: 'Extra Withholding', name: 'w4ExtraWithholding', type: 'text' },
        { label: 'Signature', name: 'w4Signature', type: 'text' },
        { label: 'Date', name: 'w4Date', type: 'date' },
      ],
    },
    {
      id: 'i9',
      title: 'I-9 Employment Eligibility Verification',
      fields: [
        { label: 'Last Name', name: 'i9LastName', type: 'text' },
        { label: 'First Name', name: 'i9FirstName', type: 'text' },
        { label: 'Middle Initial', name: 'i9MiddleInitial', type: 'text' },
        { label: 'Other Names', name: 'i9OtherNames', type: 'text' },
        { label: 'Address', name: 'i9Address', type: 'text' },
        { label: 'Apt Number', name: 'i9AptNumber', type: 'text' },
        { label: 'City', name: 'i9City', type: 'text' },
        { label: 'State', name: 'i9State', type: 'text' },
        { label: 'Zip', name: 'i9Zip', type: 'text' },
        { label: 'DOB', name: 'i9DOB', type: 'date' },
        { label: 'SSN', name: 'i9SSN', type: 'text' },
        { label: 'Email', name: 'i9Email', type: 'email' },
        { label: 'Phone', name: 'i9Phone', type: 'tel' },
        { label: 'Citizen Status', name: 'i9CitizenStatus', type: 'select', options: ['', 'US Citizen', 'Permanent Resident', 'Authorized Alien'] },
        { label: 'Alien Number', name: 'i9AlienNumber', type: 'text' },
        { label: 'Admission Number', name: 'i9AdmissionNumber', type: 'text' },
        { label: 'Foreign Passport Number', name: 'i9ForeignPassportNumber', type: 'text' },
        { label: 'Country of Issuance', name: 'i9CountryOfIssuance', type: 'text' },
        { label: 'Signature', name: 'i9Signature', type: 'text' },
        { label: 'Date', name: 'i9Date', type: 'date' },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      <div
        className="fixed top-0 left-0 right-0 h-64 bg-blue-500"
        style={{ clipPath: 'polygon(0 0, 100% 0, 100% 70%, 0 100%)', zIndex: 0 }}
      />

      <div className="relative z-10 sticky top-0 bg-white shadow-lg border-b-4 border-blue-500">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">HR Form Review</h1>
              <p className="text-sm text-gray-600 mt-1">
                {status === 'finalized'
                  ? 'Finalized - No further edits allowed'
                  : isEditable
                  ? 'Edit Mode - Changes not yet saved'
                  : 'View Mode - Read Only'}
              </p>
            </div>
            <div className="flex gap-3">
              {!isEditable && status !== 'finalized' && (
                <>
                  <button
                    onClick={handleEditClick}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white transition-all duration-300 hover:shadow-lg bg-blue-500 hover:bg-blue-700"
                  >
                    <Edit2 size={18} /> Edit Form
                  </button>
                  <button
                    onClick={() => setShowPDFPreview(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white transition-all duration-300 hover:shadow-lg bg-blue-500 hover:bg-blue-700"
                  >
                    <Eye size={18} /> Review PDF
                  </button>
                  <button
                    onClick={handleGeneratePDF}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white transition-all duration-300 hover:shadow-lg bg-blue-500 hover:bg-blue-700"
                  >
                    <DownloadCloud size={18} /> Download PDF
                  </button>
                </>
              )}

              {status === 'finalized' && (
                <>
                  <button
                    onClick={() => setShowPDFPreview(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white bg-blue-500 hover:bg-blue-700"
                  >
                    <Eye size={18} /> Review PDF
                  </button>
                  <button
                    onClick={handleGeneratePDF}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white bg-blue-500 hover:bg-blue-700"
                  >
                    <DownloadCloud size={18} /> Download PDF
                  </button>
                  <span className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-green-700 bg-green-100">
                    <Check size={18} /> Finalized
                  </span>
                </>
              )}

              {isEditable && (
                <>
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300"
                  >
                    <X size={18} /> Cancel Edit
                  </button>
                  <button
                    onClick={() => setShowPDFPreview(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white bg-blue-500 hover:bg-blue-700"
                  >
                    <Eye size={18} /> Preview PDF
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="relative z-5 max-w-7xl mx-auto px-6 py-12">
        <div className="space-y-6">
          {formData.hrSignature && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <Check className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm text-green-700">HR signature loaded successfully</span>
            </div>
          )}

          {requestMessage && (
            <div
              className={`mb-4 p-4 rounded-lg ${
                requestMessage.includes('✅')
                  ? 'bg-green-100 text-green-700'
                  : requestMessage.includes('❌')
                  ? 'bg-red-100 text-red-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {requestMessage}
            </div>
          )}

          {emailError && (
            <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">
              Error: {emailError}
            </div>
          )}

          {emailSuccess && (
            <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg flex items-center">
              <Check className="mr-2" size={20} />
              Application PDF has been emailed successfully!
            </div>
          )}

          {sections.map((section) => (
            <FormSection
              key={section.id}
              section={section}
              isExpanded={expandedSections[section.id]}
              onToggle={() => toggleSection(section.id)}
              formData={formData}
              onInputChange={handleInputChange}
              isEditable={isEditable && status !== 'finalized'}
            />
          ))}

          <SignatureSection
            data={{
              applicantSignature: formData.applicantSignature,
              caregiverSignature: formData.caregiverSignature,
              hrSignature: formData.hrSignature,
              acknowledgesDuties: formData.acknowledgesDuties,
            }}
            onChange={handleSignatureChange}
            isEditable={isEditable && status !== 'finalized'}
          />

          {!isEditable && status !== 'finalized' && (
            <div className="mt-8 p-6 bg-white rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Review Actions</h3>
              <p className="text-gray-600 mb-6">
                Once you've reviewed the application, you can send it via email, download a copy, or finalize it.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleSendReviewedEmail}
                  disabled={sendingRequest || finalizing}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 disabled:opacity-50"
                >
                  {sendingRequest ? <Loader className="w-5 h-5 animate-spin" /> : <Mail size={18} />}
                  {sendingRequest ? 'Processing...' : 'Send Reviewed Application via Email'}
                </button>
                <button
                  onClick={handleGeneratePDF}
                  disabled={sendingRequest || finalizing}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 disabled:opacity-50"
                >
                  <Download size={18} /> Download PDF
                </button>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-800 mb-1">Finalize Application</p>
                <p className="text-xs text-gray-600 mb-4">
                  Finalizing locks the form and prevents any further edits. Only do this once
                  you've verified all information is correct.
                </p>
                <button
                  onClick={handleFinalize}
                  disabled={finalizing || sendingRequest}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-black transition-all duration-200 disabled:opacity-50"
                >
                  {finalizing ? <Loader className="w-5 h-5 animate-spin" /> : <Check size={18} />}
                  {finalizing ? 'Finalizing…' : 'Finalize Application'}
                </button>
              </div>
            </div>
          )}

          {status === 'finalized' && (
            <div className="mt-8 p-6 bg-green-50 rounded-lg shadow-sm border border-green-200">
              <div className="flex items-start gap-3">
                <div className="bg-green-600 text-white rounded-full p-2 shrink-0">
                  <Check size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-green-900">Application Finalized</h3>
                  <p className="text-sm text-green-700 mt-1">
                    This application has been locked and can no longer be edited. You can still
                    download a PDF or send it via email.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-6">
                <button
                  onClick={handleSendReviewedEmail}
                  disabled={sendingRequest}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 disabled:opacity-50"
                >
                  {sendingRequest ? <Loader className="w-5 h-5 animate-spin" /> : <Mail size={18} />}
                  {sendingRequest ? 'Processing...' : 'Email PDF'}
                </button>
                <button
                  onClick={handleGeneratePDF}
                  disabled={sendingRequest}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 disabled:opacity-50"
                >
                  <Download size={18} /> Download PDF
                </button>
              </div>
            </div>
          )}

          {isEditable && (
            <div className="flex justify-end gap-3 mt-8">
              <button
                onClick={handleCancelEdit}
                className="px-6 py-3 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <Loader className="w-5 h-5 animate-spin" /> : <Check size={18} />}
                {saving ? 'Saving...' : 'Done Editing'}
              </button>
            </div>
          )}
        </div>
      </div>

      {showPDFPreview && (
        <PDFPreviewModal
          formData={formData}
          onClose={() => setShowPDFPreview(false)}
          isEditable={isEditable}
        />
      )}
    </div>
  );
};

export default HRFormPage;